import { get } from 'svelte/store';
import { acquireToken } from './auth/auth.js';
import { contextTarget, normalizeMode } from './runContext.js';

// API Service Layer
const configuredBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
// Node test runs (vitest, no DOM) have no `window`; the origin is only meaningful in a browser.
const BASE_URL = configuredBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');

/**
 * The deploy body's wire members (ADR-0040 / C8 coordinator note): the server REFUSES unknown JSON members
 * (System.Text.Json unmapped-member handling = Disallow → 400), so a stale shape — `exchange: 6` above all,
 * which would otherwise bind nowhere and silently land on Coinbase — never reaches it. Binding is
 * case-insensitive (the form sends its historical PascalCase spelling), so the guard compares case-insensitively.
 * contract: packages/AutoTrader.Run/Models/DeployStrategyRequest.cs
 */
export const DEPLOY_REQUEST_KEYS = Object.freeze([
  'strategy', 'name', 'mode', 'authorize', 'symbols', 'venue', 'frequencySeconds', 'allocations', 'portfolioRatio',
  'quoteCurrency', 'enableStopOrders', 'riskFactor', 'sizingLogic', 'benchmarkSymbol', 'maxNotionalPerOrder',
  'maxOrdersPerWindow', 'tuning', 'twap',
]);
const DEPLOY_REQUEST_KEYS_LOWER = new Set(DEPLOY_REQUEST_KEYS.map((k) => k.toLowerCase()));

class APIService {
  // The run TARGET every target-scoped call defaults to (ADR-0011 AC 8 → ADR-0040): derived from the
  // header context (Sandbox -> {6, Live}, Live -> {2, Live}), NEVER a hardcoded id — switching context
  // re-points every call that doesn't pass an explicit target. The manual trading surfaces are Live by
  // definition; the Paper Trading dashboard passes its own {2, Paper} target explicitly.
  get defaultTarget() {
    return get(contextTarget);
  }

  /** The venue id of the default target — the venue-only (market data, orders, vault) calls use this. */
  get defaultExchange() {
    return this.defaultTarget.venue;
  }

  /** The run mode of the default target — the mode-scoped (holdings, portfolio) reads use this. */
  get defaultMode() {
    return this.defaultTarget.mode;
  }

  // `options.timeoutMs` (optional) wraps the fetch in an AbortController so a slow request can be
  // cancelled — used by long-running calls like a backtest run. An aborted fetch throws a DOMException
  // with name 'AbortError', which callers can distinguish from an HTTP error to show a non-destructive
  // "still running" message rather than a hard failure. Omitting timeoutMs preserves the prior behavior
  // (no timeout) for every existing caller.
  async fetchWithErrorHandling(url, options = {}) {
    const { timeoutMs, ...fetchOptions } = options;
    let controller;
    let timeoutId;
    let didTimeout = false;
    if (timeoutMs) {
      controller = new AbortController();
      timeoutId = setTimeout(() => { didTimeout = true; controller.abort(); }, timeoutMs);
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      };

      const token = await acquireToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        ...(controller ? { signal: controller.signal } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', { status: response.status, url, body: errorText });
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      // A 202 Accepted may carry a JSON body (the backtest enqueue returns { runId } — review W1:
      // discarding it broke the whole async flow). Parse it when present; bodiless 202s stay null.
      if (response.status === 202 && contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }

      // Handle empty responses (204 No Content, bodiless 202, etc.)
      if (response.status === 204 || response.status === 202 || !contentType || !contentType.includes('application/json')) {
        return null;
      }

      return await response.json();
    } catch (error) {
      // Only a deliberate timeout should surface as the non-destructive "still running" path; a coincidental
      // AbortError from another cause keeps its identity so genuine failures aren't masked as timeouts.
      if (didTimeout && error?.name === 'AbortError') {
        console.warn('API request timed out:', url);
        throw error; // name stays 'AbortError'; caller maps it to the timeout message
      }
      console.error('API Error:', error);
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  // Market Data APIs
  async getSymbols(exchange = this.defaultExchange) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/MarketData/symbols?exchange=${exchange}`
    );
  }

  async getQuote(symbol, exchange = this.defaultExchange) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/MarketData/quote?exchange=${exchange}&symbol=${symbol}`
    );
  }

  async getOHLCV(symbol, frequency = 86400, exchange = this.defaultExchange, maxRecords = 100) {
    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 days ago
    
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/MarketData/ohlcv?exchange=${exchange}&symbol=${symbol}&frequency=${frequency}`//&startDate=${startDate}&endDate=${endDate}&maxRecords=${maxRecords}`
    );
  }

  async getTrades(symbol, exchange = this.defaultExchange) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/MarketData/trades?exchange=${exchange}&symbol=${symbol}`
    );
  }

  async getOrderBook(symbol, exchange = this.defaultExchange) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/MarketData/orderbooks?exchange=${exchange}&symbol=${symbol}`
    );
  }

  // Spot-wallet holdings APIs (Exchange.Web). Route is /api/holdings — deliberately NOT /api/portfolio,
  // which belongs exclusively to the AutoTrader-host forward (the controller used to shadow that prefix and
  // silently answered the AutoTrading Portfolio's /positions read with the wrong shape).
  //
  // Holdings are read per RUN TARGET (ADR-0040 M8): the venue account (mode=Live) or the caller's paper
  // book on that venue (mode=Paper). `mode` is REQUIRED by the server (400 without it — it never defaults
  // onto the real-money account), so the client always sends one.
  // contract: services/Exchange.Web/Controllers/PortfolioController.cs GetAllPositions(exchange, orgId, mode)
  async getPositions(exchange = this.defaultExchange, orgId = null, mode = this.defaultMode) {
    const params = new URLSearchParams({ exchange: String(exchange), mode: this._requireMode(mode) });
    if (orgId) params.append('orgId', orgId);
    return this.fetchWithErrorHandling(`${BASE_URL}/api/holdings/positions?${params}`);
  }

  /** A mode-scoped read never leaves without a recognised mode — the server would 400 it; fail here with the reason. */
  _requireMode(mode) {
    const normalized = normalizeMode(mode);
    if (!normalized) throw new Error(`A run mode (Paper | Live) is required for this read; got ${JSON.stringify(mode)}.`);
    return normalized;
  }

  // Order APIs
  async placeOrder(orderData, orgId = null) {
    const exchange = orderData.exchange || this.defaultExchange;

    // Build query parameters - using exact parameter names from the API
    const queryParams = new URLSearchParams({
      exchange: exchange.toString(),
      symbol: orderData.symbol,
      action: orderData.action.toString(),
      orderType: orderData.tradeOrderType.toString(),
      quantity: orderData.qty.toString()
    });

    // Add optional query parameters only if they have values
    if (orderData.clientOrderId) {
      queryParams.append('clientOrderId', orderData.clientOrderId);
    }
    if (orderData.price !== null && orderData.price !== undefined) {
      queryParams.append('price', orderData.price.toString());
    }
    if (orderData.amount !== null && orderData.amount !== undefined) {
      queryParams.append('amount', orderData.amount.toString());
    }
    if (orderData.stopPrice !== null && orderData.stopPrice !== undefined) {
      queryParams.append('stopPrice', orderData.stopPrice.toString());
    }

    // Add TWAP parameters
    if (orderData.enableTwap !== undefined) {
      queryParams.append('enableTwap', orderData.enableTwap.toString());
    }
    if (orderData.endBy !== null && orderData.endBy !== undefined) {
      queryParams.append('endBy', orderData.endBy);
    }

    // Add orgId if provided
    if (orgId) {
      queryParams.append('orgId', orgId);
    }

    // TASK-20260823-twap-strategies-tca (AC 26): the TWAP policy travels in the JSON body as
    // `{ twap }` (the TwapExecutionPolicy shape, pricing nested — see lib/twapPolicy.js policyToRequest);
    // the legacy `enableTwap`/`endBy` query params above are kept for the duration-only path.
    // Without a policy no body is sent at all - the API creates new RequestOrder() server-side,
    // which avoids any validation issues with the RequestOrder model (the pre-feature contract).
    const body = orderData.twap ? JSON.stringify({ twap: orderData.twap }) : undefined;
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/Order?${queryParams}`,
      {
        method: 'POST',
        ...(body ? { body } : {})
      }
    );
  }

  // ----- TWAP parents (TASK-20260823-twap-strategies-tca, PR-A2) -------------------------------
  // contract: Exchange.Data/Models/TwapReadModels.cs — TwapParentPage / TwapParentDetail /
  // TwapCancelResult, served by Exchange.Web's TwapController under /api/twap (tenant-scoped like
  // OrderController: the caller's org unless `orgId` names one they may act for).

  /**
   * Newest-first page of TWAP parents. `filters`: requestId, source ('manual'|'strategy'), symbol,
   * status (durable TwapParentStatus name), from, to (ISO), page, pageSize (≤ 200).
   */
  async getTwapParents(filters = {}, orgId = null) {
    const params = new URLSearchParams();
    for (const key of ['requestId', 'source', 'symbol', 'status', 'from', 'to', 'page', 'pageSize']) {
      const v = filters?.[key];
      if (v !== null && v !== undefined && v !== '') params.append(key, String(v));
    }
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(`${BASE_URL}/api/twap/parents${qs ? `?${qs}` : ''}`);
  }

  /** One parent: summary + policy + children + scorecard + 1-minute tape. 404 (thrown) for another tenant's id. */
  async getTwapParent(parentId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}`;
    return this.fetchWithErrorHandling(url);
  }

  // ----- PR-C2 (D9): maker-capture heatmap ------------------------------------------------------
  // contract: TwapController — GET /api/twap/heatmap?requestId&source and GET /api/twap/parents/{id}/heatmap
  // → { cells: [{ dayOfWeek (0–6, Sunday = 0), hour (0–23), makerFillPct (decimal|null), fills (int) }] },
  // aggregated over the caller's parents in the tenant scope.

  /** Day×hour maker capture over the parents matching `requestId` / `source` ('manual'|'strategy'). */
  async getTwapHeatmap(filters = {}, orgId = null) {
    const params = new URLSearchParams();
    for (const key of ['requestId', 'source']) {
      const v = filters?.[key];
      if (v !== null && v !== undefined && v !== '') params.append(key, String(v));
    }
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(`${BASE_URL}/api/twap/heatmap${qs ? `?${qs}` : ''}`);
  }

  /** The same aggregate keyed off one parent (its requestId/source; every manual parent when manual). 404 (thrown) for another tenant's id. */
  async getTwapParentHeatmap(parentId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}/heatmap?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}/heatmap`;
    return this.fetchWithErrorHandling(url);
  }

  /** Cancel a parent → { accepted, pending, status }. `pending` = the cross-host cancel request was persisted. */
  async cancelTwapParent(parentId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}/cancel?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/twap/parents/${encodeURIComponent(parentId)}/cancel`;
    return this.fetchWithErrorHandling(url, { method: 'POST' });
  }

  // The by-id / list order reads and cancel take an OPTIONAL `mode` (ADR-0040): the server defaults to Live
  // (manual trading IS Live), but the SPA always says which book it means — a Paper caller passes 'Paper',
  // without which a paper order id is looked up on the real venue and 404s.
  // contract: services/Exchange.Web/Controllers/OrderController.cs details / orders / cancel (`mode` optional)
  _orderUrl(path, params, orgId, mode) {
    const qs = new URLSearchParams({ ...params, mode: this._requireMode(mode) });
    if (orgId) qs.append('orgId', orgId);
    return `${BASE_URL}/api/Order/${path}?${qs}`;
  }

  async cancelOrder(orderId, exchange = this.defaultExchange, orgId = null, mode = this.defaultMode) {
    const url = this._orderUrl('cancel', { exchange: String(exchange), orderId: String(orderId) }, orgId, mode);
    return this.fetchWithErrorHandling(url, { method: 'POST' });
  }

  async getOrderDetails(orderId, exchange = this.defaultExchange, orgId = null, mode = this.defaultMode) {
    const url = this._orderUrl('details', { exchange: String(exchange), orderId: String(orderId) }, orgId, mode);
    return this.fetchWithErrorHandling(url);
  }

  async getOrders(symbols = '', orderIds = '', exchange = this.defaultExchange, orgId = null, mode = this.defaultMode) {
    const url = this._orderUrl('orders', { exchange: String(exchange), symbols, orderIds }, orgId, mode);
    return this.fetchWithErrorHandling(url);
  }

  // Backtest APIs
  // TASK-20260728/ADR-0018: enqueue a backtest as a BACKGROUND run. Returns 202 { runId } immediately —
  // the server collects data and runs in a hosted worker, so this request never rides out a long
  // collection and cannot time out on run length. Progress arrives over SSE (backtest-progress:
  // collecting → progress → terminal); the result is fetched by runId on completion. 409 = a run is
  // Strategy parameter policy (ADR-0024 §2): which parameters each strategy freezes, and which
  // strategies select their own universe. Tenant-invariant, so no org scope — the SPA locks form
  // fields from this instead of hardcoding strategy ids client-side.
  async getStrategyPolicies() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/strategies`);
  }

  // already queued/running for this account; 429 = queue full. The same orgId MUST be sent here as the
  // open SSE stream used (KTD3) or progress events miss the connection.
  async enqueueBacktest(request, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest/runs?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest/runs`;
    return this.fetchWithErrorHandling(url, {
      method: 'POST',
      body: JSON.stringify(request),
      timeoutMs: 15000 // enqueue-only: no exchange/orchestrator work happens in this request
    });
  }

  // The LEGACY synchronous run (frozen contract, C3): blocks for the full run, so it carries a
  // generous client timeout via AbortController. The SPA now uses enqueueBacktest; this stays for
  // contract stability and scripted use. The same-orgId rule as the SSE stream applies (KTD3).
  async runBacktest(request, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest/run?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest/run`;
    return this.fetchWithErrorHandling(url, {
      method: 'POST',
      body: JSON.stringify(request),
      timeoutMs: 180000 // 3 min — generous enough for a large in-bounds run
    });
  }

  async getBacktestRuns(orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest`;
    return this.fetchWithErrorHandling(url);
  }

  async getBacktestRun(runId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}`;
    return this.fetchWithErrorHandling(url);
  }

  async deleteBacktestRun(runId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}`;
    return this.fetchWithErrorHandling(url, { method: 'DELETE' });
  }

  // TASK-20260728: download a completed backtest run's Export ZIP (returns.csv + trades.csv + metrics.csv +
  // README). A RAW bearer fetch (the body is binary, so fetchWithErrorHandling — which JSON-decodes — must not
  // touch it), mirroring exportStrategyData. Returns { blob, filename } for a download anchor. Gated exactly
  // like getBacktestRun (owner-only; another tenant's run → 404).
  async exportBacktestData(runId, orgId = null) {
    const url = orgId
      ? `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}/export?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/backtest/${encodeURIComponent(runId)}/export`;
    const headers = {};
    const token = await acquireToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', { status: response.status, url, body: errorText });
      throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'backtest-export.zip';
    return { blob: await response.blob(), filename };
  }

  // AutoTrader APIs (U5/ADR-0011). Mirrors the backtest client: orgId as a query param, bearer header via
  // fetchWithErrorHandling, same-orgId rule as the backtest POST + SSE stream (the deploy/query calls and the
  // open SSE stream must pass the same $selectedOrgId or events miss the connection). A deploy targets a
  // RUN TARGET (ADR-0040: `venue` + `mode`); deploying Live on Coinbase additionally requires
  // `authorize: true` — the typed-name confirm supplies it (the server refuses an unauthorized real-money
  // deploy independently).
  _autotraderUrl(path, orgId) {
    return orgId
      ? `${BASE_URL}/api/autotrader${path}?orgId=${encodeURIComponent(orgId)}`
      : `${BASE_URL}/api/autotrader${path}`;
  }

  // contract: packages/AutoTrader.Run/Models/DeployStrategyRequest.cs — `venue` (int, optional, default
  // Coinbase) + `mode` (REQUIRED, the NAME string: the server answers `ModeRequired` without it and refuses a
  // number; a deploy is never retargeted by default). Unknown members are refused HERE — the retired
  // `Exchange` first and by name — so a stale form shape can never reach the wire (the server 400s it too).
  async deployStrategy(request, orgId = null) {
    const keys = Object.keys(request ?? {});
    if (keys.some((k) => k.toLowerCase() === 'exchange')) {
      throw new Error('Deploy body carries the retired `Exchange` field — send `venue` + `mode` (ADR-0040).');
    }
    const unknown = keys.filter((k) => !DEPLOY_REQUEST_KEYS_LOWER.has(k.toLowerCase()));
    if (unknown.length > 0) {
      throw new Error(`Deploy body carries members the server refuses: ${unknown.join(', ')} (ADR-0040).`);
    }
    if (!normalizeMode(request?.mode)) {
      throw new Error('Deploy body needs `mode` (the name: Paper | Live) — a deploy is never retargeted by default (ADR-0040).');
    }
    return this.fetchWithErrorHandling(this._autotraderUrl('/deploy', orgId), {
      method: 'POST',
      body: JSON.stringify(request),
      timeoutMs: 30000,
    });
  }

  async getStrategies(orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl('', orgId));
  }

  // #3/KTD6b: the caller's USD balance + live RESERVATION overview (drives the Live deploy form's per-org
  // sliders, inline ceiling, and reserved-funds visibility). Returns
  // { availableUsd, reservedUsd, freeUsd, reservations: [{strategyId, strategyName, status, orgId, reservedUsd}] }
  // (availableUsd/freeUsd null on a balance-read failure → the form degrades to free entry).
  async getAvailableBalance(exchange = this.defaultExchange, orgId = null, currency = null) {
    const base = this._autotraderUrl('/balance', orgId);
    const sep = base.includes('?') ? '&' : '?';
    // ADR-0014: `currency` scopes every figure to one quote currency (default USD server-side).
    const currencyParam = currency ? `&currency=${encodeURIComponent(currency)}` : '';
    return this.fetchWithErrorHandling(`${base}${sep}exchange=${exchange}${currencyParam}`);
  }

  async getStrategy(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}`, orgId));
  }

  async getStrategyPerformance(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/performance`, orgId));
  }

  // TASK-20260721 item 7: download the strategy's Export Data ZIP (returns.csv + trades.csv +
  // candles_signals.csv + README). A RAW bearer fetch — fetchWithErrorHandling JSON-decodes, and this
  // response is binary. Returns { blob, filename } for the caller to hand to a download anchor.
  async exportStrategyData(id, orgId = null) {
    const url = this._autotraderUrl(`/${encodeURIComponent(id)}/export`, orgId);
    const headers = {};
    const token = await acquireToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', { status: response.status, url, body: errorText });
      throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'strategy-export.zip';
    return { blob: await response.blob(), filename };
  }

  // TASK-20260728-monthly-export-decision-sse: download the §10 monthly report CSV (daily net/gross return,
  // turnover, holdings, regime + a window summary with realized-vs-modeled slippage, hit rate, exits-by-rule).
  // "Monthly" is the common case of an arbitrary from/to window (ISO-8601 strings, optional). Same RAW bearer
  // fetch as exportStrategyData (the body is text/csv, not JSON). Returns { blob, filename } for a download anchor.
  async exportMonthlyReport(id, { from = null, to = null } = {}, orgId = null) {
    // Build ONE query string (incl. orgId) like getStrategySignals — _autotraderUrl naively appends ?orgId=
    // and would produce a malformed ...?from=X?orgId=Y when the path already carries a query.
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (orgId) params.append('orgId', orgId);
    const query = params.toString();
    const url = `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/export/monthly${query ? `?${query}` : ''}`;

    const headers = {};
    const token = await acquireToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', { status: response.status, url, body: errorText });
      throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'monthly-report.csv';
    return { blob: await response.blob(), filename };
  }

  // U4/R4-R5: the strategy's current open positions (qty/avgEntry/mark/uPnL per symbol), shown on the Overview
  // tab. Pass the SAME orgId as getStrategyPerformance so the read is org-scoped (a member sees only their org's
  // positions — the backend org-isolates the ledger scan).
  async getStrategyPositions(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/positions`, orgId));
  }

  // U5/R3-R5: the strategy's durable error history (severity >= Error, newest-first, timestamped), shown on the
  // Errors tab. Pass the SAME orgId as the other reads so it is org-scoped (a member sees only their org's errors
  // plus strategy-wide ones — the backend org-isolates the read).
  async getStrategyErrors(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/errors`, orgId));
  }

  // #3: the strategy's durable ALERT history (severity < Error — Info/Warning: divergence, funds-exhausted,
  // withdrawal, candle-retry …), shown on the Alerts tab. Same org-scoping as getStrategyErrors.
  async getStrategyAlerts(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/alerts`, orgId));
  }

  // #3: most-recent-bar error/alert counts for the tab badges ({ errorCount, alertCount, barTime }). The window
  // is rows at/after the latest persisted bar; it resets as a newer bar advances. 0/0 before the first bar.
  async getAlertCounts(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/alert-counts`, orgId));
  }

  async startStrategy(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/start`, orgId), { method: 'POST' });
  }

  async pauseStrategy(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/pause`, orgId), { method: 'POST' });
  }

  async resumeStrategy(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/resume`, orgId), { method: 'POST' });
  }

  // "End" a running strategy. flatten=true closes open positions at market; false leaves them managed.
  async stopStrategy(id, { flatten = false } = {}, orgId = null) {
    const base = this._autotraderUrl(`/${encodeURIComponent(id)}/stop`, orgId);
    const sep = base.includes('?') ? '&' : '?';
    return this.fetchWithErrorHandling(`${base}${sep}flatten=${flatten ? 'true' : 'false'}`, { method: 'POST' });
  }

  // "Remove" (undeploy) a deployed/stopped strategy. Same flatten disposition contract.
  async undeployStrategy(id, { flatten = false } = {}, orgId = null) {
    const base = this._autotraderUrl(`/${encodeURIComponent(id)}/undeploy`, orgId);
    const sep = base.includes('?') ? '&' : '?';
    return this.fetchWithErrorHandling(`${base}${sep}flatten=${flatten ? 'true' : 'false'}`, { method: 'POST' });
  }

  // ---- Transparency (U8c): historical candles ⨝ indicators ⨝ signals for the Candles & Signals tab. The
  //      from/to bound the window (the server caps an unbounded pull). orgId is the membership-gated read scope.
  async getStrategySignals(id, symbol, { from = null, to = null } = {}, orgId = null) {
    const params = new URLSearchParams({ symbol });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (orgId) params.append('orgId', orgId);
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/signals?${params}`);
  }

  // ---- Researcher observability (TASK-20260728-researcher-observability-ui): the decision-lineage reads
  //      powering the Decisions / Execution / Book tabs. Same org-scoping rule as every per-strategy read.

  // The decision tape + blotter: trade_decisions newest-first, each with its terminal execution outcome(s)
  // joined server-side. Optional filters: type (entry/exit/book_snapshot/external_close), symbol, from/to.
  async getStrategyDecisions(id, { type = null, symbol = null, from = null, to = null } = {}, orgId = null) {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (symbol) params.append('symbol', symbol);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/decisions${qs ? `?${qs}` : ''}`);
  }

  // Execution quality (§10 realized-vs-modeled): { rows, stats } — stats aggregates are null (not 0) when
  // the window has no computable sample; render absence honestly.
  async getExecutionQuality(id, { from = null, to = null } = {}, orgId = null) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/execution-quality${qs ? `?${qs}` : ''}`);
  }

  // The Integrity tab's snapshot (TASK-K2T-8): server-computed invariant checks + drift table +
  // advisory ticket + cost + cadence (+ Shishito stop ladder). Read-only; same org-scoping rule as
  // every per-strategy read. Null aggregates are honest absence — render as —, never 0.
  async getStrategyIntegrity(id, orgId = null) {
    const params = new URLSearchParams();
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/integrity${qs ? `?${qs}` : ''}`);
  }

  // The lightweight tab-dot payload (TASK-K2T-15, AC 12): overall + per-check statuses only — the
  // workspace badge polls this instead of the full snapshot.
  async getIntegritySummary(id, orgId = null) {
    const params = new URLSearchParams();
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/integrity/summary${qs ? `?${qs}` : ''}`);
  }

  // The per-strategy TCA snapshot (TASK-K2T-15, ADR-0030): execution marks vs the independent
  // witness tape, the policy A/B scoreboard, and the capacity frontier.
  async getStrategyTca(id, orgId = null) {
    const params = new URLSearchParams();
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/tca${qs ? `?${qs}` : ''}`);
  }

  // Whole-book snapshots (one per rebalance period), oldest-first for direct charting.
  async getBookSnapshots(id, { from = null, to = null } = {}, orgId = null) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (orgId) params.append('orgId', orgId);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/book${qs ? `?${qs}` : ''}`);
  }

  // The run's frozen strategy-version identity ({ available, version }) — available:false is the honest
  // "this run captured no lineage" state, NOT an error.
  async getLineageVersion(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/lineage-version`, orgId));
  }

  // The steering/audit feed (deploy/start/stop/params/cash actions with actor + outcome), newest-first.
  async getSteeringEvents(id, { from = null, to = null } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/autotrader/${encodeURIComponent(id)}/steering${qs ? `?${qs}` : ''}`);
  }

  // ---- Runtime capital management (U7/KTD4). Each accept returns 202 + { adjustmentId }; the UI then watches
  //      the Pending→Applied/Failed lifecycle over SSE (U10) / the getAdjustments poll fallback. ----
  async addCash(id, orgId, amount, scopeOrgId = null) {
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/orgs/${encodeURIComponent(orgId)}/cash`, scopeOrgId),
      { method: 'POST', body: JSON.stringify({ amount, withdrawAll: false }) });
  }
  async withdraw(id, orgId, amount, scopeOrgId = null) {
    // Withdraw is a negative amount (U7 accepts the signed amount; the worker clamps at apply time).
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/orgs/${encodeURIComponent(orgId)}/cash`, scopeOrgId),
      { method: 'POST', body: JSON.stringify({ amount: -Math.abs(amount), withdrawAll: false }) });
  }
  async withdrawAll(id, orgId, scopeOrgId = null) {
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/orgs/${encodeURIComponent(orgId)}/cash`, scopeOrgId),
      { method: 'POST', body: JSON.stringify({ amount: 0, withdrawAll: true }) });
  }
  async addOrg(id, { orgId, startingCash, riskFactor = null, cashBufferRate = null }, scopeOrgId = null) {
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/orgs`, scopeOrgId),
      { method: 'POST', body: JSON.stringify({ orgId, startingCash, riskFactor, cashBufferRate }) });
  }
  async removeOrg(id, orgId, scopeOrgId = null) {
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/orgs/${encodeURIComponent(orgId)}`, scopeOrgId),
      { method: 'DELETE' });
  }
  async getAdjustments(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/adjustments`, orgId));
  }
  // The participating-org allocation slices (+ latest per-org NAV/PnL) for the Allocation tab (U14).
  async getOrgSlices(id, orgId = null) {
    return this.fetchWithErrorHandling(this._autotraderUrl(`/${encodeURIComponent(id)}/orgs`, orgId));
  }

  // ---- Live-editable params (U12/KTD13): an UpdateParams adjustment through the same async-audit path. The
  //      server re-validates the editable subset against its bounds; structural params are rejected. ----
  async updateStrategyParams(id, patch, orgId = null) {
    return this.fetchWithErrorHandling(
      this._autotraderUrl(`/${encodeURIComponent(id)}/params`, orgId),
      { method: 'POST', body: JSON.stringify(patch) });
  }

  // ==================== Portfolio (U10b/U15) — hosted in the AutoTrader host, same-origin via the forward ====
  // Every portfolio read is scoped to ONE RUN TARGET (ADR-0011 → ADR-0040: `exchange` + `mode`, mode
  // REQUIRED by the server) — targets are never combined: a Paper and a Live book on the same venue are two
  // portfolios. `orgId` may be a single id (string) or an array of selected orgs (U3/U4 — repeated
  // ?orgId=a&orgId=b). The server consolidates across the SELECTED orgs, validating membership +
  // participation before any ledger read.
  // contract: services/AutoTrader/Controllers/PortfolioController.cs (exchange, orgId[], mode)
  _portfolioUrl(path, orgId, target = null) {
    const params = new URLSearchParams();
    const orgs = Array.isArray(orgId) ? orgId : (orgId ? [orgId] : []);
    for (const o of orgs) {
      if (o) params.append('orgId', o);
    }
    if (target != null) {
      params.append('exchange', String(target.venue));
      params.append('mode', this._requireMode(target.mode));
    }
    const qs = params.toString();
    return qs ? `${BASE_URL}/api/portfolio${path}?${qs}` : `${BASE_URL}/api/portfolio${path}`;
  }

  async getPortfolioPerformance(orgId = null, target = this.defaultTarget) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/performance', orgId, target));
  }
  async getPortfolioBlotter(orgId = null, target = this.defaultTarget) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/blotter', orgId, target));
  }
  async getDiscoverable(orgId = null, target = this.defaultTarget) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/discoverable', orgId, target));
  }
  // U8/KTD8: the consolidated holdings — { totalNav, positions[] } across the SELECTED orgs' participating
  // strategies, per-symbol net from each strategy's ledger (cost-basis preserving). Target-scoped.
  async getPortfolioPositions(orgId = null, target = this.defaultTarget) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/positions', orgId, target));
  }
  // U4/U5: the orgs the caller may select for a consolidated view (intersection of membership ∩ participation ∩
  // target), membership-filtered server-side. Populates the header org multi-select.
  async getParticipatingOrgs(target = this.defaultTarget) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/participating-orgs', null, target));
  }
  // Signed amount (+ deposit, − withdraw); fans out one Pending adjustment per administrable strategy (U9).
  async portfolioCash(amount, orgId = null) {
    return this.fetchWithErrorHandling(this._portfolioUrl('/cash', orgId),
      { method: 'POST', body: JSON.stringify({ amount }) });
  }
  async joinStrategy(id, startingCash, orgId = null) {
    return this.fetchWithErrorHandling(this._portfolioUrl(`/strategies/${encodeURIComponent(id)}/join`, orgId),
      { method: 'POST', body: JSON.stringify({ startingCash }) });
  }

  // Vault APIs
  async getVaultAccounts(orgId) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/Vault/secrets/${orgId}/accounts`
    );
  }

  async getVaultSecret(orgId, exchange, accountId) {
    const accountIdPart = accountId ? `/${encodeURIComponent(accountId)}` : '';
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/Vault/secrets/${orgId}/${exchange}${accountIdPart}`
    );
  }

  async setVaultSecret(orgId, exchange, accountId, accountNickName, credentials) {
    // Build URL with query parameters for optional accountId and accountNickName
    const params = new URLSearchParams();

    if (accountId) {
      params.append('accountId', accountId);
    }

    if (accountNickName) {
      params.append('accountNickName', accountNickName);
    }

    const queryString = params.toString();
    const url = `${BASE_URL}/api/Vault/secrets/${encodeURIComponent(orgId)}/${exchange}${queryString ? '?' + queryString : ''}`;

    // Deliberately not logged: this payload carries the raw API secret, and console output is
    // readable by anything running in the page and survives in browser history (C4).
    return this.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        body: JSON.stringify(credentials)
      }
    );
  }

  async deleteVaultSecret(orgId, exchange, accountId) {
    const accountIdPart = accountId ? `/${encodeURIComponent(accountId)}` : '';
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/Vault/secrets/${orgId}/${exchange}${accountIdPart}`,
      {
        method: 'DELETE'
      }
    );
  }

  // ==================== User Management APIs ====================

  // Roles
  async getRoles() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/roles`);
  }

  // Orgs
  async getOrgs() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/orgs`);
  }

  async getOrg(orgId) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/orgs/${encodeURIComponent(orgId)}`);
  }

  async updateOrg(orgId, org) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/orgs/${encodeURIComponent(orgId)}`, {
      method: 'PUT',
      body: JSON.stringify(org)
    });
  }

  // UserRoles
  async getUserRoles() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-roles`);
  }

  async getUserRole(userId) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-roles/${encodeURIComponent(userId)}`);
  }

  async createUserRole(userRole) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-roles`, {
      method: 'POST',
      body: JSON.stringify(userRole)
    });
  }

  async updateUserRole(userId, userRole) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-roles/${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: JSON.stringify(userRole)
    });
  }

  async deleteUserRole(userId) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-roles/${encodeURIComponent(userId)}`, {
      method: 'DELETE'
    });
  }

  // UserOrgs
  async getUserOrgs() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-orgs`);
  }

  async getUserOrgsByUserId(userId) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-orgs/${encodeURIComponent(userId)}`);
  }

  async createUserOrg(userOrg) {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/user-orgs`, {
      method: 'POST',
      body: JSON.stringify(userOrg)
    });
  }

  async deleteUserOrg(userId, orgId) {
    return this.fetchWithErrorHandling(
      `${BASE_URL}/api/UserManagement/user-orgs/${encodeURIComponent(userId)}/${encodeURIComponent(orgId)}`,
      {
        method: 'DELETE'
      }
    );
  }

  // Current User
  async getMyRole() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/me/role`);
  }

  async getMyOrgs() {
    return this.fetchWithErrorHandling(`${BASE_URL}/api/UserManagement/me/orgs`);
  }
}

export default new APIService();
